import { Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SocialReportDialogComponent } from "../social-report-dialog/social-report-dialog.component";
import { SocialBlockDialogComponent } from "../social-block-dialog/social-block-dialog.component";
import { Location } from "@angular/common";
import { MessageService } from "primeng/api";
import { TranslocoService } from "@ngneat/transloco";
import { AuthService } from "src/app/service/auth.service";
import { SocialService } from "src/app/service/social.service";
import { checkUpload, uploadRejectI18n, UploadCheck } from "src/app/shared/upload/upload-policy";
import { PublicService } from "src/app/public/service.service";
import { ProjectService } from "src/app/service/project.service";
import {
  AvatarUploadResult,
  ProjectSearchHitResponse,
  SocialFollowUserInfo,
  SocialProfileResponse,
  UpdateProfileRequest,
} from "src/app/model/social/social.types";
import { ProjectSummaryResponse } from "src/app/model/response/ProjectSummaryResponse";
import { GetAllPublicProjectsResponse } from "src/app/public/public-project-response";
import { sharingProjectType } from "src/app/model/enum/sharingProjectType";
import { ImageUrlService } from "src/app/service/image-url.service";
import { DEFAULT_IMAGE } from "src/app/model/mockup/image-mockup";

@Component({
  selector: "app-social-profile",
  templateUrl: "./social-profile.component.html",
  styleUrls: ["./social-profile.component.css"],
})
export class SocialProfileComponent implements OnInit {
  userId = "";
  profile: SocialProfileResponse | null = null;
  publicProjects: ProjectSummaryResponse[] = [];
  linkProjects: ProjectSummaryResponse[] = [];
  privateProjects: ProjectSummaryResponse[] = [];
  loading = true;
  projectsLoading = false;
  currentUserId?: string;

  /** Menu "⋯" do visitante (denunciar conta). */
  showProfileActions = false;
  @ViewChild("profileKebab") private profileKebab?: ElementRef<HTMLElement>;
  @ViewChild("reportDialog") private reportDialog?: SocialReportDialogComponent;

  /** Fecha o menu ao clicar em qualquer ponto fora dele (o clique no próprio botão só alterna). */
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showProfileActions) return;
    const target = event.target as Node | null;
    if (target && this.profileKebab?.nativeElement.contains(target)) return;
    this.showProfileActions = false;
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.showProfileActions = false;
  }

  reportProfile(): void {
    this.showProfileActions = false;
    this.reportDialog?.open("User", this.userId);
  }

  // ----- Projetos que sigo (só o dono: a API expõe a lista apenas em /me) -----
  /** Páginas já carregadas, na ordem da API (mais recentes primeiro). */
  followedProjects: ProjectSearchHitResponse[] = [];
  /** Total informado pela API — é o que a estatística "seguindo" mostra. */
  followedTotal = 0;
  followedLoading = false;
  followedDialogVisible = false;
  private followedPage = 0;
  private static readonly FOLLOWED_PAGE_SIZE = 20;

  get hasMoreFollowed(): boolean {
    return this.followedProjects.length < this.followedTotal;
  }

  /** Primeira página junto com o perfil: dá o total da estatística e já preenche o modal. */
  loadFollowedProjects(): void {
    this.followedProjects = [];
    this.followedTotal = 0;
    this.followedPage = 0;
    if (this.isOwnProfile) this.loadMoreFollowed();
  }

  /** Próxima página; ignora chamadas com carga em voo ou já com tudo carregado. */
  loadMoreFollowed(): void {
    if (this.followedLoading) return;
    if (this.followedPage > 0 && !this.hasMoreFollowed) return;
    this.followedLoading = true;
    const page = this.followedPage + 1;
    this.social.getMyFollowedProjectsPage(page, SocialProfileComponent.FOLLOWED_PAGE_SIZE).subscribe({
      next: (res) => {
        // Seguir um projeto entre duas páginas desloca as seguintes: dedupe por id.
        const seen = new Set(this.followedProjects.map((p) => p.projectId));
        const fresh = (res?.items ?? []).filter((p) => !seen.has(p.projectId));
        this.followedProjects = [...this.followedProjects, ...fresh];
        this.followedTotal = res?.total ?? this.followedProjects.length;
        this.followedPage = page;
        this.followedLoading = false;
      },
      error: () => {
        this.followedLoading = false;
      },
    });
  }

  /** Rolagem do modal chegando ao fim busca a próxima página. */
  onFollowedListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el || !this.hasMoreFollowed || this.followedLoading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) this.loadMoreFollowed();
  }

  openFollowedProjects(): void {
    if (!this.isOwnProfile) return;
    this.followedDialogVisible = true;
  }

  /** Clique num projeto da lista: fecha o diálogo e abre o perfil do projeto. */
  openFollowedProject(p: ProjectSearchHitResponse): void {
    this.followedDialogVisible = false;
    this.openProjectProfile(p.projectId);
  }

  followedVehicle(p: ProjectSearchHitResponse): string {
    return [p.vehicleManufacturer, p.vehicleModel].filter(Boolean).join(' ');
  }

  // ----- Bloqueio -----
  /** Perfil respondeu 404: inexistente OU oculto pelo bloqueio (a API não distingue de propósito). */
  profileUnavailable = false;
  @ViewChild("blockDialog") private blockDialog?: SocialBlockDialogComponent;
  blockedDialogVisible = false;
  blockedLoading = false;
  blockedUsers: SocialFollowUserInfo[] = [];
  /** Usuário cujo desbloqueio está em voo — mostra spinner na linha e trava os demais botões. */
  unblockingUserId: string | null = null;

  /** Abre a confirmação de bloqueio (explica o efeito; o diálogo faz a chamada e mostra o spinner). */
  blockProfile(): void {
    this.showProfileActions = false;
    const name = this.profile?.nickname || this.profile?.name || this.userId;
    this.blockDialog?.open(this.userId, name);
  }

  /** Bloqueio confirmado: a partir daí nenhum dos dois vê o outro, então volta ao feed. */
  onBlocked(): void {
    this.router.navigate(["/feed"]);
  }

  openBlockedUsers(): void {
    this.showProfileActions = false;
    this.blockedDialogVisible = true;
    this.blockedLoading = true;
    this.social.getBlockedUsers(1, 100).subscribe({
      next: (res) => {
        this.blockedUsers = res?.users ?? [];
        this.blockedLoading = false;
      },
      error: () => {
        this.blockedUsers = [];
        this.blockedLoading = false;
      },
    });
  }

  unblock(u: SocialFollowUserInfo): void {
    if (this.unblockingUserId) return;
    this.unblockingUserId = u.userId;
    this.social.unblockUser(u.userId).subscribe({
      next: () => {
        this.unblockingUserId = null;
        this.blockedUsers = this.blockedUsers.filter((x) => x.userId !== u.userId);
        this.messageService.add({ severity: "success", summary: this.translocoService.translate("socialFeed.unblockDone") });
      },
      error: (err) => {
        this.unblockingUserId = null;
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate("socialFeed.unblock"),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
  }

  blockedDisplay(u: SocialFollowUserInfo): string {
    return u.nickname || u.name || (u.userId ? u.userId.substring(0, 8) + "…" : "?");
  }

  // Edit profile modal
  editVisible = false;
  editNickname = "";
  editBio = "";
  editWebsite = "";
  avatarPreviewUrl: string | null = null;
  pendingAvatarMediaId: string | null = null;
  isUploadingAvatar = false;
  avatarCropperVisible = false;
  avatarFileToCrop: File | null = null;
  isSaving = false;
  saveError: string | null = null;
  /** Nickname original carregado do servidor (usado para enviar só se mudou). */
  private originalNickname = "";
  /** 409 do backend: nickname já em uso. */
  nicknameConflict = false;
  private static readonly NICKNAME_MAX_LENGTH = 100;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private social: SocialService,
    private publicService: PublicService,
    private projectService: ProjectService,
    private auth: AuthService,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private imageUrlService: ImageUrlService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.currentUserId = this.auth.getUserInformations();
    this.route.paramMap.subscribe((pm) => {
      const id = pm.get("userId");
      if (!id) {
        this.router.navigate(["/feed"]);
        return;
      }
      this.userId = id;
      this.loadAll();
    });
  }

  projectThumbStyle(pr: ProjectSummaryResponse): string {
    const img = pr.attributes?.primaryImage || pr.primaryImage;
    if (img && img !== "image12") {
      return this.imageUrlService.projectStyle(this.userId, pr.projectID, img, 'cardThumb');
    }
    return 'url("' + DEFAULT_IMAGE.href + '")';
  }

  get isOwnProfile(): boolean {
    return !!this.currentUserId && this.currentUserId === this.userId;
  }

  get avatarInitial(): string {
    const src = this.profile?.nickname || this.profile?.name || "?";
    return src.charAt(0).toUpperCase();
  }

  get badgesCount(): number {
    return this.profile?.badgesCount ?? 0;
  }

  get marketplaceItemsCount(): number {
    return this.profile?.marketplaceItemsCount ?? 0;
  }

  get displayProjectsCount(): number {
    return this.isOwnProfile
      ? this.publicProjects.length + this.linkProjects.length + this.privateProjects.length
      : (this.profile?.projectsCount ?? 0);
  }

  loadAll(): void {
    this.loading = true;
    this.profileUnavailable = false;
    this.social.getProfile(this.userId).subscribe({
      next: (p) => {
        this.profile = p;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        // 404 cobre "não existe" e "oculto por bloqueio/sanção": a tela mostra o mesmo aviso neutro.
        if ((err as { status?: number })?.status === 404) {
          this.profileUnavailable = true;
          return;
        }
        this.messageService.add({
          severity: "error",
          summary: this.translocoService.translate('socialFeed.profileLabel'),
          detail: SocialService.parseHttpErrorMessage(err),
        });
      },
    });
    this.loadProjects();
    this.loadFollowedProjects();
  }

  loadProjects(): void {
    this.projectsLoading = true;
    if (this.isOwnProfile) {
      this.projectService.findAllProjectSummary(this.userId).subscribe({
        next: (all) => {
          const list = all ?? [];
          this.publicProjects = list.filter((p) => Number(p.sharingProjectTypeID) === sharingProjectType.PUBLIC);
          this.linkProjects = list.filter((p) => Number(p.sharingProjectTypeID) === sharingProjectType.PUBLIC_LINK);
          this.privateProjects = list.filter((p) => Number(p.sharingProjectTypeID) === sharingProjectType.PRIVATE);
          this.projectsLoading = false;
        },
        error: () => {
          this.projectsLoading = false;
          this.publicProjects = this.linkProjects = this.privateProjects = [];
        },
      });
    } else {
      this.publicService.getAllPublicProjects(sharingProjectType.PUBLIC, null, undefined, this.userId).subscribe({
        next: (response) => {
          const items = response?.listItens ?? [];
          this.publicProjects = items
            .filter((p: GetAllPublicProjectsResponse) => p.userID === this.userId)
            .map((p: GetAllPublicProjectsResponse) => this.toProjectSummary(p));
          this.linkProjects = [];
          this.privateProjects = [];
          this.projectsLoading = false;
        },
        error: () => {
          this.projectsLoading = false;
          this.publicProjects = this.linkProjects = this.privateProjects = [];
        },
      });
    }
  }

  private toProjectSummary(p: GetAllPublicProjectsResponse): ProjectSummaryResponse {
    return new ProjectSummaryResponse(
      p.projectID,
      p.projectName,
      false,
      p.attributes?.primaryImage ?? p.primaryImage?.[0]?.name ?? "",
      p.userID,
      [],
      p.nickname ? [p.nickname] : [],
      p.attributes ?? undefined,
      p.userID ?? "",
      p.likes ?? 0,
      [],
      "",
      false,
      p.sharingProjectTypeID
    );
  }

  openEditModal(): void {
    this.editNickname = this.profile?.nickname ?? "";
    this.originalNickname = this.editNickname;
    this.editBio = this.profile?.bio ?? "";
    this.editWebsite = this.profile?.website ?? "";
    this.avatarPreviewUrl = this.profile?.avatarUrl ?? null;
    this.pendingAvatarMediaId = null;
    this.saveError = null;
    this.nicknameConflict = false;
    this.editVisible = true;
  }

  /** Limpa alerta de duplicidade quando o usuário recomeça a digitar. */
  onNicknameChange(): void {
    if (this.nicknameConflict) {
      this.nicknameConflict = false;
    }
  }

  get nicknameTooLong(): boolean {
    return this.editNickname.length > SocialProfileComponent.NICKNAME_MAX_LENGTH;
  }

  get nicknameEmpty(): boolean {
    return this.editNickname.trim().length === 0;
  }

  get canSaveProfile(): boolean {
    if (this.isUploadingAvatar || this.isSaving) return false;
    if (this.nicknameEmpty || this.nicknameTooLong) return false;
    return true;
  }

  /** Toast padrão de upload rejeitado (tipo/tamanho) usando a política central. */
  private notifyUploadRejected(chk: UploadCheck): void {
    const m = uploadRejectI18n(chk);
    this.messageService.add({
      severity: 'error',
      summary: this.translocoService.translate(m.summaryKey),
      detail: this.translocoService.translate(m.detailKey, { '0': m.param }),
    });
  }

  onAvatarSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const chk = checkUpload(file, 'avatar');
    if (!chk.ok) {
      input.value = '';
      this.notifyUploadRejected(chk);
      return;
    }

    input.value = ''; // permite re-selecionar o mesmo arquivo depois
    this.avatarFileToCrop = file;
    this.avatarCropperVisible = true;
  }

  /** Recebe a foto já recortada (1:1) do diálogo e faz preview + upload (register + multipart). */
  onAvatarCropped(file: File): void {
    const previousPreview = this.avatarPreviewUrl;
    const newPreview = URL.createObjectURL(file);
    this.avatarPreviewUrl = newPreview;
    this.isUploadingAvatar = true;

    // Em falha (registro ou binário), reverte o preview para não dar "falso sucesso"
    // visual — a imagem não deve parecer trocada se o upload não concluiu.
    const fail = (detailKey: string) => {
      this.isUploadingAvatar = false;
      URL.revokeObjectURL(newPreview);
      this.avatarPreviewUrl = previousPreview;
      this.pendingAvatarMediaId = null;
      this.messageService.add({ severity: "error", summary: "Avatar", detail: this.translocoService.translate(detailKey) });
    };

    this.social.registerAvatarUpload(file.name).subscribe({
      next: (result: AvatarUploadResult) => {
        this.social.uploadFileMultipart(this.currentUserId!, result.objectKey, file).then(() => {
          this.pendingAvatarMediaId = result.mediaId;
          this.isUploadingAvatar = false;
        }).catch(() => fail('socialFeed.avatarError'));
      },
      error: () => fail('socialFeed.avatarUploadError'),
    });
  }

  saveProfile(): void {
    if (!this.canSaveProfile) return;
    this.isSaving = true;
    this.saveError = null;
    this.nicknameConflict = false;

    const payload: UpdateProfileRequest = {
      bio: this.editBio || null,
      website: this.editWebsite || null,
    };
    if (this.pendingAvatarMediaId) {
      payload.avatarMediaId = this.pendingAvatarMediaId;
    }
    // Só envia nickname se mudou (e estiver preenchido) — backend rejeita "" com 400.
    const trimmedNick = this.editNickname.trim();
    if (trimmedNick && trimmedNick !== this.originalNickname) {
      payload.nickname = trimmedNick;
    }

    this.social.updateProfile(payload).subscribe({
      next: (updated) => {
        // Se um novo avatar foi enviado, usar o blob URL local para evitar race condition
        // com a propagação do S3. O URL do servidor é carregado na próxima visita.
        const localBlobUrl = this.pendingAvatarMediaId ? this.avatarPreviewUrl : null;
        this.profile = { ...updated, avatarUrl: localBlobUrl ?? updated.avatarUrl };
        this.originalNickname = updated.nickname ?? "";
        this.pendingAvatarMediaId = null;
        this.isSaving = false;
        this.editVisible = false;
        this.messageService.add({ severity: "success", summary: this.translocoService.translate('socialFeed.profileUpdated') });
      },
      error: (err) => {
        this.isSaving = false;
        if (err?.status === 409) {
          this.nicknameConflict = true;
        } else {
          this.saveError = SocialService.parseHttpErrorMessage(err);
        }
      },
    });
  }

  /** Navega para o perfil social do projeto (não para a edição). */
  openProjectProfile(projectId: string): void {
    // Passa a origem (perfil) para o breadcrumb montar o trilho "Social Feed > Minha Conta > Projeto"
    // e permitir voltar ao perfil pelo próprio breadcrumb.
    this.router.navigate(["/feed/project", projectId], { queryParams: { fromUser: this.userId } });
  }

  /** Volta para a tela ANTERIOR em vez de forçar o feed. */
  goBackToFeed(): void {
    if (window.history.length > 1) this.location.back();
    else this.router.navigate(["/feed"]);
  }
}
